import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Plus, Clock, MapPin, Users, Video, Phone,
  ChevronLeft, ChevronRight, X, Edit2, Trash2, CheckCircle,
  XCircle, FileText, Repeat
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../Toast';
import { useConfirm } from '../../../hooks/useConfirm';
import FirebaseStaffMeetingService, {
  StaffMeetingData,
  CreateMeetingRequest
} from '../../../services/firebase/FirebaseStaffMeetingService';
import FirebaseAppointmentService from '../../../services/firebase/FirebaseAppointmentService';
import TeamMemberService, { TeamMember } from '../../../services/TeamMemberService';
import VoiceInputButton from '../../VoiceInputButton';

interface MeetingsTabProps {
  practiceOwnerId: string;
  onJoinMeetingVideo?: (meetingId: string, meetingTitle: string) => void;
}

type ViewMode = 'calendar' | 'list';
type ListFilter = 'upcoming' | 'past' | 'cancelled' | 'all';

const meetingService = FirebaseStaffMeetingService.getInstance();
const teamService = TeamMemberService.getInstance();
const appointmentService = FirebaseAppointmentService.getInstance();

// Check if a time range overlaps with an appointment
const timesOverlap = (
  meetStart: string, meetEnd: string,
  apptStart: string, apptEnd: string
): boolean => {
  const toMin = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const ms = toMin(meetStart), me = toMin(meetEnd || meetStart) || ms + 60;
  const as = toMin(apptStart), ae = toMin(apptEnd || apptStart) || as + 60;
  return ms < ae && as < me;
};

const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Check if a meeting can be joined (15 min before start to 30 min after end)
const canJoinMeeting = (meeting: StaffMeetingData): { canJoin: boolean; reason?: string; minutesUntil?: number } => {
  if (meeting.status !== 'scheduled' && meeting.status !== 'in-progress') {
    return { canJoin: false, reason: 'Meeting is not active' };
  }
  const now = new Date();
  const [sh, sm] = meeting.startTime.split(':').map(Number);
  const [eh, em] = (meeting.endTime || meeting.startTime).split(':').map(Number);
  const meetingStart = new Date(meeting.date + 'T00:00:00');
  meetingStart.setHours(sh, sm, 0, 0);
  const meetingEnd = new Date(meeting.date + 'T00:00:00');
  meetingEnd.setHours(eh, em, 0, 0);

  const joinWindowStart = new Date(meetingStart.getTime() - 15 * 60000); // 15 min before
  const joinWindowEnd = new Date(meetingEnd.getTime() + 30 * 60000); // 30 min after

  if (now < joinWindowStart) {
    const minutesUntil = Math.ceil((joinWindowStart.getTime() - now.getTime()) / 60000);
    return { canJoin: false, reason: `Meeting starts in ${minutesUntil} minutes`, minutesUntil };
  }
  if (now > joinWindowEnd) {
    return { canJoin: false, reason: 'Meeting window has passed' };
  }
  return { canJoin: true };
};

const MeetingsTab: React.FC<MeetingsTabProps> = ({ practiceOwnerId, onJoinMeetingVideo }) => {
  const { user } = useAuth();
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();

  const [meetings, setMeetings] = useState<StaffMeetingData[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [listFilter, setListFilter] = useState<ListFilter>('upcoming');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<StaffMeetingData | null>(null);
  const [editMode, setEditMode] = useState(false);

  // Availability: map of memberId -> appointment title they're busy with
  const [busyMembers, setBusyMembers] = useState<Record<string, string>>({});

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    date: '',
    startTime: '09:00',
    endTime: '10:00',
    type: 'video' as 'video' | 'in-person' | 'phone',
    location: '',
    attendeeIds: [] as string[],
    agenda: '',
    recurring: 'none' as 'none' | 'weekly' | 'biweekly' | 'monthly'
  });

  // Load meetings + team members
  useEffect(() => {
    if (!practiceOwnerId) return;
    setLoading(true);
    const unsubscribe = meetingService.subscribeToMeetings(practiceOwnerId, (data) => {
      setMeetings(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [practiceOwnerId]);

  useEffect(() => {
    setTeamMembers(teamService.getAllTeamMembers());
    const listener = () => setTeamMembers(teamService.getAllTeamMembers());
    teamService.subscribe(listener);
    return () => teamService.unsubscribe(listener);
  }, []);

  // 15-minute reminder for upcoming meetings
  useEffect(() => {
    if (meetings.length === 0) return;
    const remindedIds = new Set<string>();
    const checkReminders = () => {
      const now = new Date();
      for (const meeting of meetings) {
        if (meeting.status !== 'scheduled' || remindedIds.has(meeting.id)) continue;
        const [sh, sm] = meeting.startTime.split(':').map(Number);
        const meetStart = new Date(meeting.date + 'T00:00:00');
        meetStart.setHours(sh, sm, 0, 0);
        const diff = meetStart.getTime() - now.getTime();
        // Remind if meeting starts within 15 minutes (but not already past)
        if (diff > 0 && diff <= 15 * 60000) {
          remindedIds.add(meeting.id);
          const mins = Math.ceil(diff / 60000);
          toast.info(
            `Meeting in ${mins} min`,
            `"${meeting.title}" starts at ${meeting.startTime}`
          );
        }
      }
    };
    checkReminders();
    const interval = setInterval(checkReminders, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [meetings, toast]);

  // Check team member availability when date/time changes in the form
  useEffect(() => {
    if (!form.date || !form.startTime || !showScheduleModal) {
      setBusyMembers({});
      return;
    }
    let cancelled = false;
    const checkAvailability = async () => {
      try {
        const dateStart = new Date(form.date + 'T00:00:00');
        const dateEnd = new Date(form.date + 'T23:59:59');
        // Fetch all appointments for the selected date across all providers
        const allAppts = await appointmentService.getAppointments({
          startDate: dateStart,
          endDate: dateEnd
        });
        if (cancelled) return;
        const activeAppts = allAppts.filter(
          (a) => a.status === 'scheduled' || a.status === 'confirmed' || a.status === 'in-progress'
        );
        const busy: Record<string, string> = {};
        for (const member of teamMembers) {
          if (!member.authUid) continue;
          const conflict = activeAppts.find(
            (a) =>
              a.providerId === member.authUid &&
              timesOverlap(form.startTime, form.endTime, a.startTime, a.endTime)
          );
          if (conflict) {
            busy[member.id] = conflict.patientName
              ? `In Session (${conflict.patientName})`
              : 'In Session';
          }
        }
        if (!cancelled) setBusyMembers(busy);
      } catch (err) {
        console.error('[MeetingsTab] Availability check error:', err);
      }
    };
    checkAvailability();
    return () => { cancelled = true; };
  }, [form.date, form.startTime, form.endTime, showScheduleModal, teamMembers]);

  // Calendar helpers
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(currentYear, currentMonth, 0).getDate();
    const days: { day: number; month: number; year: number; isCurrentMonth: boolean }[] = [];

    // Previous month padding
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        month: currentMonth - 1,
        year: currentMonth === 0 ? currentYear - 1 : currentYear,
        isCurrentMonth: false
      });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ day: d, month: currentMonth, year: currentYear, isCurrentMonth: true });
    }
    // Next month padding
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push({
        day: d,
        month: currentMonth + 1,
        year: currentMonth === 11 ? currentYear + 1 : currentYear,
        isCurrentMonth: false
      });
    }
    return days;
  }, [currentMonth, currentYear]);

  const getMeetingsForDate = (dateStr: string) =>
    meetings.filter((m) => m.date === dateStr && m.status !== 'cancelled');

  const formatDateStr = (day: number, month: number, year: number) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const today = new Date();
  const todayStr = formatDateStr(today.getDate(), today.getMonth(), today.getFullYear());

  // List filtering
  const filteredMeetings = useMemo(() => {
    const now = new Date().toISOString().slice(0, 10);
    switch (listFilter) {
      case 'upcoming':
        return meetings.filter((m) => m.date >= now && m.status !== 'cancelled');
      case 'past':
        return meetings.filter((m) => m.date < now || m.status === 'completed');
      case 'cancelled':
        return meetings.filter((m) => m.status === 'cancelled');
      default:
        return meetings;
    }
  }, [meetings, listFilter]);

  // Form handlers
  const resetForm = () => {
    setForm({
      title: '', description: '', date: '', startTime: '09:00', endTime: '10:00',
      type: 'video', location: '', attendeeIds: [], agenda: '', recurring: 'none'
    });
    setEditMode(false);
    setSelectedMeeting(null);
  };

  const openScheduleModal = (dateStr?: string) => {
    resetForm();
    if (dateStr) setForm((f) => ({ ...f, date: dateStr }));
    setShowScheduleModal(true);
  };

  const openEditModal = (meeting: StaffMeetingData) => {
    setForm({
      title: meeting.title,
      description: meeting.description,
      date: meeting.date,
      startTime: meeting.startTime,
      endTime: meeting.endTime,
      type: meeting.type,
      location: meeting.location,
      attendeeIds: meeting.attendeeIds,
      agenda: meeting.agenda,
      recurring: meeting.recurring
    });
    setSelectedMeeting(meeting);
    setEditMode(true);
    setShowScheduleModal(true);
  };

  const openDetailModal = (meeting: StaffMeetingData) => {
    setSelectedMeeting(meeting);
    setShowDetailModal(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.date || !form.startTime) {
      toast.error('Missing fields', 'Title, date, and start time are required.');
      return;
    }

    const attendeeNames = form.attendeeIds.map((id) => {
      const member = teamMembers.find((m) => m.id === id);
      return member?.name || 'Unknown';
    });

    try {
      if (editMode && selectedMeeting) {
        await meetingService.updateMeeting(selectedMeeting.id, {
          ...form,
          attendeeNames
        });
        toast.success('Meeting updated', `"${form.title}" has been updated.`);
      } else {
        const request: CreateMeetingRequest = {
          ...form,
          practiceOwnerId,
          attendeeNames,
          notes: '',
          createdBy: user?.uid || '',
          createdByName: user?.displayName || 'Provider'
        };
        await meetingService.createMeeting(request);
        toast.success('Meeting scheduled', `"${form.title}" has been scheduled.`);
      }
      setShowScheduleModal(false);
      resetForm();
    } catch (err) {
      console.error('[MeetingsTab] Save error:', err);
      toast.error('Error', 'Failed to save meeting. Please try again.');
    }
  };

  const handleDelete = async (meeting: StaffMeetingData) => {
    if (!await confirm({ title: 'Delete Meeting', message: `Delete "${meeting.title}"? This cannot be undone.`, confirmLabel: 'Delete', variant: 'danger' })) return;
    try {
      await meetingService.deleteMeeting(meeting.id);
      toast.success('Deleted', 'Meeting has been deleted.');
      setShowDetailModal(false);
    } catch (err) {
      toast.error('Error', 'Failed to delete meeting.');
    }
  };

  const handleCancel = async (meeting: StaffMeetingData) => {
    try {
      await meetingService.cancelMeeting(meeting.id);
      toast.success('Cancelled', `"${meeting.title}" has been cancelled.`);
      setShowDetailModal(false);
    } catch (err) {
      toast.error('Error', 'Failed to cancel meeting.');
    }
  };

  const handleComplete = async (meeting: StaffMeetingData) => {
    try {
      await meetingService.completeMeeting(meeting.id);
      toast.success('Completed', `"${meeting.title}" marked as completed.`);
      setShowDetailModal(false);
    } catch (err) {
      toast.error('Error', 'Failed to complete meeting.');
    }
  };

  const toggleAttendee = (memberId: string) => {
    setForm((f) => ({
      ...f,
      attendeeIds: f.attendeeIds.includes(memberId)
        ? f.attendeeIds.filter((id) => id !== memberId)
        : [...f.attendeeIds, memberId]
    }));
  };

  const typeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video size={14} />;
      case 'phone': return <Phone size={14} />;
      default: return <MapPin size={14} />;
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return '#3b82f6';
      case 'in-progress': return '#f59e0b';
      case 'completed': return '#22c55e';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // ============ RENDER ============

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem'
      }}>
        <h2 style={{ margin: 0, color: '#f9fafb', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Calendar size={22} /> Staff Meetings
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {/* View Toggle */}
          <div style={{
            display: 'flex', borderRadius: '8px', overflow: 'hidden',
            border: '1px solid #475569'
          }}>
            <button
              onClick={() => setViewMode('calendar')}
              style={{
                padding: '8px 16px', border: 'none', cursor: 'pointer',
                backgroundColor: viewMode === 'calendar' ? '#3b82f6' : '#1e293b',
                color: '#f8fafc', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              <Calendar size={14} /> Calendar
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px', border: 'none', cursor: 'pointer',
                backgroundColor: viewMode === 'list' ? '#3b82f6' : '#1e293b',
                color: '#f8fafc', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              <FileText size={14} /> List
            </button>
          </div>
          <button
            onClick={() => openScheduleModal()}
            style={{
              padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
              fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            <Plus size={16} /> Schedule Meeting
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
          Loading meetings...
        </div>
      ) : viewMode === 'calendar' ? (
        /* ============ CALENDAR VIEW ============ */
        <div style={{
          backgroundColor: '#1e293b', borderRadius: '12px', border: '1px solid #334155',
          overflow: 'hidden'
        }}>
          {/* Month Navigation */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px 20px', borderBottom: '1px solid #334155'
          }}>
            <button onClick={() => {
              if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear((y) => y - 1); }
              else setCurrentMonth((m) => m - 1);
            }} style={{
              background: 'none', border: '1px solid #475569', borderRadius: '6px',
              padding: '6px 10px', color: '#f8fafc', cursor: 'pointer'
            }}>
              <ChevronLeft size={16} />
            </button>
            <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '18px' }}>
              {MONTHS[currentMonth]} {currentYear}
            </h3>
            <button onClick={() => {
              if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear((y) => y + 1); }
              else setCurrentMonth((m) => m + 1);
            }} style={{
              background: 'none', border: '1px solid #475569', borderRadius: '6px',
              padding: '6px 10px', color: '#f8fafc', cursor: 'pointer'
            }}>
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day Headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {DAYS.map((day) => (
              <div key={day} style={{
                padding: '10px', textAlign: 'center', color: '#94a3b8',
                fontSize: '12px', fontWeight: 600, borderBottom: '1px solid #334155',
                textTransform: 'uppercase', letterSpacing: '0.05em'
              }}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {calendarDays.map((cd, idx) => {
              const dateStr = formatDateStr(cd.day, cd.month, cd.year);
              const dayMeetings = getMeetingsForDate(dateStr);
              const isToday = dateStr === todayStr;
              return (
                <div
                  key={idx}
                  onClick={() => openScheduleModal(dateStr)}
                  style={{
                    minHeight: '90px', padding: '6px',
                    borderRight: (idx + 1) % 7 !== 0 ? '1px solid #334155' : 'none',
                    borderBottom: '1px solid #334155',
                    backgroundColor: isToday ? 'rgba(59,130,246,0.08)' : 'transparent',
                    cursor: 'pointer', transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { if (!isToday) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
                  onMouseLeave={(e) => { if (!isToday) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <div style={{
                    fontSize: '13px', fontWeight: isToday ? 700 : 400,
                    color: !cd.isCurrentMonth ? '#475569' : isToday ? '#3b82f6' : '#e2e8f0',
                    marginBottom: '4px',
                    display: 'flex', justifyContent: 'flex-end'
                  }}>
                    <span style={{
                      width: '24px', height: '24px', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', borderRadius: '50%',
                      backgroundColor: isToday ? '#3b82f6' : 'transparent',
                      color: isToday ? '#fff' : undefined
                    }}>
                      {cd.day}
                    </span>
                  </div>
                  {dayMeetings.slice(0, 2).map((m) => (
                    <div
                      key={m.id}
                      onClick={(e) => { e.stopPropagation(); openDetailModal(m); }}
                      style={{
                        fontSize: '11px', padding: '2px 6px', marginBottom: '2px',
                        borderRadius: '4px', backgroundColor: statusColor(m.status) + '22',
                        color: statusColor(m.status), borderLeft: `3px solid ${statusColor(m.status)}`,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        cursor: 'pointer'
                      }}
                    >
                      {m.startTime} {m.title}
                    </div>
                  ))}
                  {dayMeetings.length > 2 && (
                    <div style={{ fontSize: '10px', color: '#94a3b8', paddingLeft: '6px' }}>
                      +{dayMeetings.length - 2} more
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* ============ LIST VIEW ============ */
        <div>
          {/* Filter Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {(['upcoming', 'past', 'cancelled', 'all'] as ListFilter[]).map((f) => (
              <button key={f} onClick={() => setListFilter(f)} style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                backgroundColor: listFilter === f ? '#3b82f6' : '#1e293b',
                color: '#f8fafc', fontSize: '13px', textTransform: 'capitalize'
              }}>
                {f} ({f === 'all' ? meetings.length :
                  f === 'upcoming' ? meetings.filter((m) => m.date >= todayStr && m.status !== 'cancelled').length :
                  f === 'past' ? meetings.filter((m) => m.date < todayStr || m.status === 'completed').length :
                  meetings.filter((m) => m.status === 'cancelled').length})
              </button>
            ))}
          </div>

          {filteredMeetings.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '60px', color: '#94a3b8',
              backgroundColor: '#1e293b', borderRadius: '12px', border: '1px solid #334155'
            }}>
              <Calendar size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{ margin: '0 0 4px 0' }}>No meetings found</p>
              <p style={{ margin: 0, fontSize: '13px', opacity: 0.7 }}>
                Click "Schedule Meeting" to create one.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredMeetings.map((meeting) => (
                <div
                  key={meeting.id}
                  onClick={() => openDetailModal(meeting)}
                  style={{
                    padding: '16px 20px', backgroundColor: '#1e293b', borderRadius: '10px',
                    border: '1px solid #334155', cursor: 'pointer', transition: 'border-color 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#334155'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <h4 style={{ margin: 0, color: '#f8fafc', fontSize: '15px' }}>{meeting.title}</h4>
                        <span style={{
                          backgroundColor: statusColor(meeting.status) + '22',
                          color: statusColor(meeting.status), padding: '2px 8px',
                          borderRadius: '4px', fontSize: '11px', textTransform: 'capitalize'
                        }}>
                          {meeting.status}
                        </span>
                        {meeting.recurring !== 'none' && (
                          <span style={{
                            display: 'flex', alignItems: 'center', gap: '3px',
                            color: '#94a3b8', fontSize: '11px'
                          }}>
                            <Repeat size={12} /> {meeting.recurring}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '16px', color: '#94a3b8', fontSize: '13px', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={13} />
                          {new Date(meeting.date + 'T00:00:00').toLocaleDateString('en-US', {
                            weekday: 'short', month: 'short', day: 'numeric'
                          })}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={13} /> {formatTime12Hour(meeting.startTime)} – {formatTime12Hour(meeting.endTime)}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {typeIcon(meeting.type)} {meeting.type}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Users size={13} /> {meeting.attendeeNames.length} attendees
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============ SCHEDULE / EDIT MODAL ============ */}
      {showScheduleModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          padding: '20px'
        }} onClick={() => { setShowScheduleModal(false); resetForm(); }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#1e293b', borderRadius: '16px', border: '1px solid #334155',
              width: '100%', maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto',
              padding: '24px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#f8fafc' }}>
                {editMode ? 'Edit Meeting' : 'Schedule New Meeting'}
              </h3>
              <button onClick={() => { setShowScheduleModal(false); resetForm(); }} style={{
                background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px'
              }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Title */}
              <div>
                <label style={labelStyle}>Meeting Title *</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g., Weekly Team Standup"
                  style={inputStyle}
                />
              </div>

              {/* Description */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Description</label>
                  <VoiceInputButton onResult={text => setForm(f => ({ ...f, description: f.description + ' ' + text }))} />
                </div>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of the meeting"
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>

              {/* Date + Time */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Date *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Start Time *</label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>End Time</label>
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Type + Location */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Meeting Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as any }))}
                    style={inputStyle}
                  >
                    <option value="video">Video Call</option>
                    <option value="in-person">In Person</option>
                    <option value="phone">Phone Call</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Location</label>
                  <input
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    placeholder={form.type === 'video' ? 'Video link (auto-generated)' : 'Office, Room #, etc.'}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Recurring */}
              <div>
                <label style={labelStyle}>Recurring</label>
                <select
                  value={form.recurring}
                  onChange={(e) => setForm((f) => ({ ...f, recurring: e.target.value as any }))}
                  style={inputStyle}
                >
                  <option value="none">One-time meeting</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {/* Attendees */}
              <div>
                <label style={labelStyle}>
                  Attendees ({form.attendeeIds.length} selected)
                  {form.date && form.startTime && (
                    <span style={{ fontWeight: 400, color: '#64748b', marginLeft: '8px' }}>
                      — showing availability for {form.date} at {form.startTime}
                    </span>
                  )}
                </label>
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '12px',
                  backgroundColor: '#0f172a', borderRadius: '8px', border: '1px solid #334155',
                  maxHeight: '180px', overflowY: 'auto'
                }}>
                  {teamMembers.length === 0 ? (
                    <span style={{ color: '#64748b', fontSize: '13px' }}>No team members found</span>
                  ) : teamMembers.map((member) => {
                    const selected = form.attendeeIds.includes(member.id);
                    const busyInfo = busyMembers[member.id];
                    const isBusy = !!busyInfo;
                    return (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => toggleAttendee(member.id)}
                        title={isBusy ? busyInfo : 'Available'}
                        style={{
                          padding: '6px 12px', borderRadius: '20px', fontSize: '12px',
                          border: selected
                            ? isBusy ? '1px solid #f59e0b' : '1px solid #3b82f6'
                            : isBusy ? '1px solid #ef444466' : '1px solid #475569',
                          backgroundColor: selected
                            ? isBusy ? '#f59e0b22' : '#3b82f622'
                            : isBusy ? '#ef444411' : 'transparent',
                          color: isBusy
                            ? selected ? '#fbbf24' : '#f87171'
                            : selected ? '#93c5fd' : '#cbd5e1',
                          cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '6px'
                        }}
                      >
                        {isBusy && (
                          <span style={{
                            width: '6px', height: '6px', borderRadius: '50%',
                            backgroundColor: '#ef4444', flexShrink: 0
                          }} />
                        )}
                        {!isBusy && form.date && (
                          <span style={{
                            width: '6px', height: '6px', borderRadius: '50%',
                            backgroundColor: '#22c55e', flexShrink: 0
                          }} />
                        )}
                        {member.name}
                        {selected && ' ✓'}
                        {isBusy && (
                          <span style={{ fontSize: '10px', opacity: 0.8 }}>
                            ({busyInfo})
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {Object.keys(busyMembers).length > 0 && (
                  <div style={{
                    marginTop: '6px', fontSize: '11px', color: '#94a3b8',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#22c55e' }} />
                      Available
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                      In Session — you can still add them
                    </span>
                  </div>
                )}
              </div>

              {/* Agenda */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Agenda</label>
                  <VoiceInputButton onResult={text => setForm(f => ({ ...f, agenda: f.agenda + ' ' + text }))} />
                </div>
                <textarea
                  value={form.agenda}
                  onChange={(e) => setForm((f) => ({ ...f, agenda: e.target.value }))}
                  placeholder="Meeting agenda items..."
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                <button
                  onClick={() => { setShowScheduleModal(false); resetForm(); }}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', border: '1px solid #475569',
                    backgroundColor: 'transparent', color: '#e2e8f0', cursor: 'pointer', fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  style={{
                    padding: '10px 24px', borderRadius: '8px', border: 'none',
                    backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
                    fontSize: '14px', fontWeight: 600
                  }}
                >
                  {editMode ? 'Update Meeting' : 'Schedule Meeting'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ DETAIL MODAL ============ */}
      {showDetailModal && selectedMeeting && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowDetailModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#1e293b', borderRadius: '16px', border: '1px solid #334155',
              width: '100%', maxWidth: '540px', padding: '24px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', color: '#f8fafc' }}>{selectedMeeting.title}</h3>
                <span style={{
                  backgroundColor: statusColor(selectedMeeting.status) + '22',
                  color: statusColor(selectedMeeting.status), padding: '3px 10px',
                  borderRadius: '4px', fontSize: '12px', textTransform: 'capitalize'
                }}>
                  {selectedMeeting.status}
                </span>
              </div>
              <button onClick={() => setShowDetailModal(false)} style={{
                background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer'
              }}>
                <X size={20} />
              </button>
            </div>

            {selectedMeeting.description && (
              <p style={{ color: '#cbd5e1', fontSize: '14px', marginBottom: '16px' }}>
                {selectedMeeting.description}
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              <div style={detailRow}>
                <Calendar size={16} style={{ color: '#94a3b8' }} />
                <span style={{ color: '#e2e8f0' }}>
                  {new Date(selectedMeeting.date + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                  })}
                </span>
              </div>
              <div style={detailRow}>
                <Clock size={16} style={{ color: '#94a3b8' }} />
                <span style={{ color: '#e2e8f0' }}>
                  {formatTime12Hour(selectedMeeting.startTime)} – {formatTime12Hour(selectedMeeting.endTime)}
                </span>
              </div>
              <div style={detailRow}>
                {typeIcon(selectedMeeting.type)}
                <span style={{ color: '#e2e8f0', textTransform: 'capitalize' }}>
                  {selectedMeeting.type}{selectedMeeting.location ? ` — ${selectedMeeting.location}` : ''}
                </span>
              </div>
              {selectedMeeting.recurring !== 'none' && (
                <div style={detailRow}>
                  <Repeat size={16} style={{ color: '#94a3b8' }} />
                  <span style={{ color: '#e2e8f0', textTransform: 'capitalize' }}>
                    {selectedMeeting.recurring}
                  </span>
                </div>
              )}
              <div style={detailRow}>
                <Users size={16} style={{ color: '#94a3b8' }} />
                <span style={{ color: '#e2e8f0' }}>
                  {selectedMeeting.attendeeNames.length > 0
                    ? selectedMeeting.attendeeNames.join(', ')
                    : 'No attendees assigned'}
                </span>
              </div>
            </div>

            {selectedMeeting.agenda && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#94a3b8', fontSize: '12px', textTransform: 'uppercase' }}>
                  Agenda
                </h4>
                <p style={{
                  color: '#cbd5e1', fontSize: '13px', whiteSpace: 'pre-wrap',
                  backgroundColor: '#0f172a', padding: '12px', borderRadius: '8px',
                  border: '1px solid #334155', margin: 0
                }}>
                  {selectedMeeting.agenda}
                </p>
              </div>
            )}

            {selectedMeeting.notes && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#94a3b8', fontSize: '12px', textTransform: 'uppercase' }}>
                  Meeting Notes
                </h4>
                <p style={{
                  color: '#cbd5e1', fontSize: '13px', whiteSpace: 'pre-wrap',
                  backgroundColor: '#0f172a', padding: '12px', borderRadius: '8px',
                  border: '1px solid #334155', margin: 0
                }}>
                  {selectedMeeting.notes}
                </p>
              </div>
            )}

            {/* Actions */}
            {selectedMeeting.status === 'scheduled' && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {/* Join button — visible to everyone, time-gated */}
                {selectedMeeting.type === 'video' && (() => {
                  const joinCheck = canJoinMeeting(selectedMeeting);
                  return (
                    <button
                      onClick={() => {
                        if (!joinCheck.canJoin) {
                          toast.info('Not Yet', joinCheck.reason || 'Cannot join at this time');
                          return;
                        }
                        if (onJoinMeetingVideo) {
                          onJoinMeetingVideo(selectedMeeting.id, selectedMeeting.title);
                          setShowDetailModal(false);
                        } else if (selectedMeeting.location) {
                          window.open(selectedMeeting.location, '_blank');
                        }
                      }}
                      title={joinCheck.canJoin ? 'Join now' : joinCheck.reason}
                      style={{
                        ...actionBtnStyle(joinCheck.canJoin ? '#8b5cf6' : '#475569'),
                        opacity: joinCheck.canJoin ? 1 : 0.6
                      }}
                    >
                      <Video size={14} />
                      {joinCheck.canJoin
                        ? 'Join Meeting'
                        : joinCheck.minutesUntil
                          ? `Join in ${joinCheck.minutesUntil}m`
                          : 'Join Meeting'}
                    </button>
                  );
                })()}
                {/* Join for non-video meetings (in-person/phone) — just informational */}
                {selectedMeeting.type !== 'video' && (() => {
                  const joinCheck = canJoinMeeting(selectedMeeting);
                  return joinCheck.minutesUntil ? (
                    <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={14} /> Starts in {joinCheck.minutesUntil} minutes
                    </span>
                  ) : null;
                })()}
                {/* Management actions — only for the meeting creator or practice owner */}
                {(selectedMeeting.createdBy === user?.uid || practiceOwnerId === user?.uid) && (
                  <>
                    <button onClick={() => { setShowDetailModal(false); openEditModal(selectedMeeting); }}
                      style={actionBtnStyle('#3b82f6')}>
                      <Edit2 size={14} /> Edit
                    </button>
                    <button onClick={() => handleComplete(selectedMeeting)}
                      style={actionBtnStyle('#22c55e')}>
                      <CheckCircle size={14} /> Mark Complete
                    </button>
                    <button onClick={() => handleCancel(selectedMeeting)}
                      style={actionBtnStyle('#f59e0b')}>
                      <XCircle size={14} /> Cancel
                    </button>
                    <button onClick={() => handleDelete(selectedMeeting)}
                      style={actionBtnStyle('#ef4444')}>
                      <Trash2 size={14} /> Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {ConfirmDialog}
    </div>
  );
};

// Shared styles
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '13px', color: '#94a3b8',
  marginBottom: '6px', fontWeight: 500
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', backgroundColor: '#0f172a',
  border: '1px solid #334155', borderRadius: '8px', color: '#f8fafc',
  fontSize: '14px', outline: 'none', boxSizing: 'border-box'
};

const detailRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px'
};

const actionBtnStyle = (color: string): React.CSSProperties => ({
  padding: '8px 16px', borderRadius: '8px', border: `1px solid ${color}33`,
  backgroundColor: `${color}18`, color, cursor: 'pointer', fontSize: '13px',
  fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px'
});

export default MeetingsTab;
